/**
 * Bid Detail Modal
 *
 * Displays complete bid information in a modal dialog.
 * PURELY PRESENTATIONAL - all data comes from hooks.
 *
 * DDD Compliance:
 * - Uses useBidDetail hook for data
 * - No business logic in component
 * - Displays view model from application layer
 * - Handles loading and error states
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useBidDetail } from '@/application/service-provider/hooks/useBidData';
import type { BidDetailViewModel } from '@/application/service-provider/view-models/BidViewModel';
import { EditBidModal } from './EditBidModal';
import { WithdrawBidModal } from './WithdrawBidModal';

interface BidDetailModalProps {
  bidId: string;
  onClose: () => void;
}

export function BidDetailModal({ bidId, onClose }: BidDetailModalProps) {
  const { data, isLoading, error } = useBidDetail(bidId);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {isLoading && <LoadingState />}
          {error && <ErrorState message={error.message} onClose={onClose} />}
          {data && (
            <BidDetailContent
              bid={data}
              onClose={onClose}
              onEdit={() => setShowEditModal(true)}
              onWithdraw={() => setShowWithdrawModal(true)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      {showEditModal && data && (
        <EditBidModal
          bid={data}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            // The bid detail will auto-refresh due to cache invalidation
          }}
        />
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && data && (
        <WithdrawBidModal
          bidId={data.id}
          onClose={() => setShowWithdrawModal(false)}
          onSuccess={() => {
            setShowWithdrawModal(false);
            onClose(); // Close detail modal after withdrawal
          }}
        />
      )}
    </>
  );
}

function BidDetailContent({
  bid,
  onClose,
  onEdit,
  onWithdraw,
}: {
  bid: BidDetailViewModel;
  onClose: () => void;
  onEdit: () => void;
  onWithdraw: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <DialogTitle className="text-2xl">{bid.projectName}</DialogTitle>
            <DialogDescription className="text-base mt-1 flex items-center gap-2">
              <span className="font-mono text-sm">{bid.planDisplayId}</span>
              <CopyButton text={bid.planId} />
            </DialogDescription>
          </div>
          <StatusBadge badge={bid.statusBadge} />
        </div>
      </DialogHeader>

      <div className="space-y-6 mt-6">
        {/* Project Details */}
        <Section title="Project Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bid.projectAddress !== 'N/A' && (
              <InfoField label="Address" value={bid.projectAddress} fullWidth />
            )}
            <InfoField label="Project Type" value={bid.projectDetails.projectType} />
            <InfoField label="Estimated Waste" value={bid.projectDetails.estimatedWaste} />
            <InfoField
              label="Target Diversion Rate"
              value={bid.projectDetails.targetDiversionRate}
            />
            <InfoField label="Start Date" value={bid.projectDetails.startDate} />
            {bid.projectDetails.targetCompletionDate && (
              <InfoField
                label="Target Completion"
                value={bid.projectDetails.targetCompletionDate}
              />
            )}
          </div>
        </Section>

        {/* Your Proposal */}
        <Section title="Your Proposal">
          {/* Service Description */}
          {bid.serviceDescription && (
            <div className="mb-4 p-3 bg-neutral-50 rounded-lg">
              <p className="text-sm font-medium text-neutral-700 mb-1">
                Service Description
              </p>
              <p className="text-sm text-neutral-900 whitespace-pre-wrap">
                {bid.serviceDescription}
              </p>
            </div>
          )}

          {/* Message to Applicant */}
          {bid.message && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900 mb-1">
                Message to Applicant
              </p>
              <p className="text-sm text-blue-800 whitespace-pre-wrap">{bid.message}</p>
            </div>
          )}
        </Section>

        {/* Pricing Breakdown */}
        <Section title="Pricing Breakdown">
          {/* Hauling Rate */}
          {bid.fullPricing.haulingRate && (
            <div className="mb-4 p-3 bg-neutral-50 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-neutral-700">Hauling Rate</p>
                  <p className="text-lg font-semibold text-neutral-900">
                    {bid.fullPricing.haulingRate.displayText}
                  </p>
                </div>
                {bid.fullPricing.haulingRate.estimatedCost && (
                  <div className="text-right">
                    <p className="text-xs text-neutral-600">
                      Est. {bid.fullPricing.haulingRate.estimatedTons} tons
                    </p>
                    <p className="text-sm font-semibold text-neutral-900">
                      {bid.fullPricing.haulingRate.estimatedCost}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Container Rentals */}
          {bid.fullPricing.containerRentals.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-neutral-700 mb-2">
                Container Rentals
              </p>
              <div className="space-y-2">
                {bid.fullPricing.containerRentals.map((rental) => (
                  <div
                    key={rental.size}
                    className="flex justify-between items-center p-3 bg-neutral-50 rounded"
                  >
                    <span className="text-sm text-neutral-900">{rental.displayText}</span>
                    <span className="text-sm font-semibold text-neutral-900">
                      {rental.totalCost}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Services */}
          {bid.fullPricing.additionalServices &&
            bid.fullPricing.additionalServices.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-neutral-700 mb-2">
                  Additional Services
                </p>
                <div className="space-y-2">
                  {bid.fullPricing.additionalServices.map((service, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-3 bg-neutral-50 rounded"
                    >
                      <span className="text-sm text-neutral-900">
                        {service.description}
                      </span>
                      <span className="text-sm font-semibold text-neutral-900">
                        {service.cost}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Summary */}
          <div className="border-t border-neutral-200 pt-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-700">Subtotal</span>
              <span className="text-sm font-medium text-neutral-900">
                {bid.fullPricing.summary.subtotal}
              </span>
            </div>
            {bid.fullPricing.summary.fees && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-700">Fees</span>
                <span className="text-sm font-medium text-neutral-900">
                  {bid.fullPricing.summary.fees}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-neutral-200">
              <span className="text-base font-semibold text-neutral-900">
                Total Estimate
              </span>
              <span className="text-lg font-bold text-neutral-900">
                {bid.fullPricing.summary.total}
              </span>
            </div>
          </div>
        </Section>

        {/* Timeline */}
        <Section title="Timeline">
          <div className="space-y-3">
            {bid.timeline.statusHistory.map((item, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 pb-3 border-b border-neutral-200 last:border-0"
              >
                <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-medium text-neutral-900 capitalize">
                      {item.status}
                    </p>
                    <p className="text-xs text-neutral-500">{item.timestamp}</p>
                  </div>
                  {item.note && (
                    <p className="text-sm text-neutral-600 mt-1">{item.note}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Response Information */}
          {bid.response && (
            <div
              className={`mt-4 p-3 rounded-lg ${
                bid.response.status === 'accepted'
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              <p
                className={`text-sm font-semibold mb-1 ${
                  bid.response.status === 'accepted' ? 'text-green-900' : 'text-red-900'
                }`}
              >
                {bid.response.status === 'accepted' ? 'Bid Accepted' : 'Bid Rejected'}
              </p>
              <p
                className={`text-xs mb-2 ${
                  bid.response.status === 'accepted' ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {bid.response.respondedAt}
              </p>
              {bid.response.message && (
                <p className="text-sm text-neutral-900">{bid.response.message}</p>
              )}
            </div>
          )}
        </Section>
      </div>

      <DialogFooter className="mt-6 gap-2">
        {bid.canEdit && (
          <button className="btn-outline" onClick={onEdit}>
            Edit Bid
          </button>
        )}
        {bid.canWithdraw && (
          <button
            className="btn-outline text-red-600 hover:bg-red-50"
            onClick={onWithdraw}
          >
            Withdraw Bid
          </button>
        )}
        <button className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </DialogFooter>
    </>
  );
}

// Helper Components

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
        {title}
      </h3>
      {children}
    </div>
  );
}

function InfoField({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: string | undefined;
  fullWidth?: boolean;
}) {
  const displayValue = value ?? 'Data pending';

  return (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <dt className="text-sm font-medium text-neutral-600 mb-1">{label}</dt>
      <dd
        className={`text-sm ${value ? 'text-neutral-900' : 'text-neutral-400 italic'}`}
      >
        {displayValue}
      </dd>
    </div>
  );
}

function StatusBadge({
  badge,
}: {
  badge: { label: string; color: string; icon: string };
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  }[badge.color] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorClasses}`}>
      {badge.label}
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="text-xs text-primary-600 hover:text-primary-700"
      title="Copy full Plan ID"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6 animate-pulse">
      <div>
        <div className="h-8 bg-neutral-200 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
      </div>
      {[1, 2, 3, 4].map((i) => (
        <div key={i}>
          <div className="h-6 bg-neutral-200 rounded w-32 mb-3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-12 bg-neutral-100 rounded"></div>
            <div className="h-12 bg-neutral-100 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="text-center py-8">
      <div className="text-red-600 text-4xl mb-4">⚠️</div>
      <h3 className="text-lg font-medium text-neutral-900 mb-2">
        Unable to Load Bid Details
      </h3>
      <p className="text-sm text-neutral-600 mb-4">{message}</p>
      <button className="btn-primary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}
